import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import Skeleton from "react-loading-skeleton";
import { Table } from "react-bootstrap";
import ContentWrapper from "../../components/contentwrapper/ContentWrapper";
import ReportNavBar from "../../components/ReportNavBar";
import withAuth from "../../utils/withAuth";
import { fetchCallAllDataByIDAPI } from "../../utils/api";
import { numberFormat } from "../../utils/numberFormat";

const HourWiseReport = () => {
    const navigate = useNavigate();
    const ppcid = localStorage.getItem("ppcdata");

    const [expandedDays, setExpandedDays] = useState(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [process, setProcess] = useState([]);

    // ✅ date helpers
    const fmtDate = (d) => {
        const yyyy = d.getFullYear();
        const mm = String(d.getMonth() + 1).padStart(2, "0");
        const dd = String(d.getDate()).padStart(2, "0");
        return `${yyyy}-${mm}-${dd}`;
    };

    const getToday = () => fmtDate(new Date());

    const [selectedDate, setSelectedDate] = useState(() => getToday());
    const [appliedDate, setAppliedDate] = useState(() => getToday());

    // ✅ hour range (1..24)
    const [fromHour, setFromHour] = useState(1);
    const [toHour, setToHour] = useState(24);

    const [appliedHours, setAppliedHours] = useState({ from: 1, to: 24 });

    // ✅ dropdowns
    const [teamOpen, setTeamOpen] = useState(false);
    const [centerOpen, setCenterOpen] = useState(false);
    const [skillOpen, setSkillOpen] = useState(false);

    const [teamSearch, setTeamSearch] = useState("");
    const [centerSearch, setCenterSearch] = useState("");
    const [skillSearch, setSkillSearch] = useState("");

    const [selectedTeams, setSelectedTeams] = useState(new Set());
    const [appliedTeams, setAppliedTeams] = useState(new Set());

    const [selectedCenters, setSelectedCenters] = useState(new Set());
    const [appliedCenters, setAppliedCenters] = useState(new Set());

    const [selectedSkills, setSelectedSkills] = useState(new Set());
    const [appliedSkills, setAppliedSkills] = useState(new Set());

    const wrapRef = useRef(null);

    const toggleDay = (dKey) => {
        setExpandedDays((prev) => {
            const next = new Set(prev);
            if (next.has(dKey)) next.delete(dKey);
            else next.add(dKey);
            return next;
        });
    };

    // ✅ center map (suffix)
    const PERSON_BY_CODE = {
        MP: "REWA",
        DI: "DINESH",
        PU: "PUSHPENDAR",
        RI: "RICHA",
        MA: "MAHADESH",
        MY: "MYSORE CENTER",
        KRL: "KERALA",
        PL: "RAVI",
        TN: "MAHALAXMI",
        MU: "MURGESH",
        RCP: "RAHUL",
        G19: "KANTA",
        G10: "RAVI",
        SCP: "SHRINIWAS",
        CH: "RAVI",
        TRK: "TARAK",
        BA: "BANGLORE",
        SL: "SHAILENDRA",
        NOI: "ABHISHEK",
        CT: "CT",
        AM: "AMIT",
        KA: "KANIKA",
        SRK: "SHAHRUKH",
        JGT: "JAGAT",
        RNOI: "RAVI NOIDA",
        GNOI: "RAVI NOIDA",
        JS: "PUSHPENDAR",
        NI: "PUSHPENDAR",
        AN: "PUSHPENDAR",
        GZB: "GHAZIABAD",
        BL: "BALLARY",
        RV: "RAVINDRA",
        KO: "KOHAT NORTH",
        MN: "RAHUL",
        BU: "BURARI",
        D2C: "D2C",
        WA: "WHATSAPP",
        WH: "WHATSAPP",
        UN: "MAYANK",
        VP: "VIKAS PURI",
        SP: "SANJAY ASTRO",
        TG: "TG",
        NSP: "NSP",
        KOL: "KOLKATA",
        RI_MA: "MAHADESH",
        RCP_MN: "RAHUL MOTI NAGAR",
    };

    // ✅ team map (prefix)
    const TEAM_BY_CODE = {
        K: "Vivek Kumar",
        R: "Pragya Singh",
        J: "Akash Deep",
        V: "Pradeep Verma",
        T: "Yanshi",
        N: "Deepika Sisodia",
        D: "Deepak Kumar Giri",
        S: "Sumit Saini",
        I: "Ranjan Kumar",
        B: "Navin Chaurasia",
        E: "Bhumika Gola",
        G: "Vimal Jadon",
        P: "Anurodh Baral",
        M: "Simrati Kaur",
        Z: "Manprit Sir",
        A: "Ankita",
        W: "Ishika Pokhriyal",
        U: "Om Kumar",
        L: "Navin Chaurasia",
        Q: "Akash Deep",
    };

    // ✅ normalizers
    const normalizeSkill = (raw) => String(raw || "").trim().toUpperCase();

    const deriveTeamName = (skillRaw) => {
        const s = normalizeSkill(skillRaw);
        if (!s) return "Other";
        const parts = s.split("_").filter(Boolean);
        const first = (parts[0] || "").toUpperCase();
        return TEAM_BY_CODE[first] || "Other";
    };

    const deriveCenterName = (skillRaw) => {
        const s = normalizeSkill(skillRaw);
        if (!s) return "Other";
        const parts = s.split("_").filter(Boolean);
        const n = parts.length;
        const last = (parts[n - 1] || "").toUpperCase();
        const last2 = n >= 2 ? `${(parts[n - 2] || "").toUpperCase()}_${last}` : null;

        if (last2 && PERSON_BY_CODE[last2]) return PERSON_BY_CODE[last2];
        if (PERSON_BY_CODE[last]) return PERSON_BY_CODE[last];
        return "Other";
    };

    const uniqSorted = (arr) =>
        Array.from(new Set((arr || []).filter(Boolean))).sort((a, b) =>
            String(a).localeCompare(String(b))
        );

    const filterOptions = (opts, q) => {
        const s = String(q || "").trim().toLowerCase();
        if (!s) return opts;
        return opts.filter((x) => String(x).toLowerCase().includes(s));
    };

    // ✅ close dropdown on outside click
    useEffect(() => {
        const onDoc = (e) => {
            if (!wrapRef.current) return;
            if (!wrapRef.current.contains(e.target)) {
                setTeamOpen(false);
                setCenterOpen(false);
                setSkillOpen(false);
            }
        };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    // ✅ API loader (date + hour range)
    const loadReport = async (id, date, h1, h2) => {
        setIsLoading(true);
        try {
            const qs = new URLSearchParams();
            qs.set("date", date || getToday());
            qs.set("start_hour", String(h1 || 1));
            qs.set("end_hour", String(h2 || 24));

            const res = await fetchCallAllDataByIDAPI(
                `ppc-team-get-hour-wise-call-report-with-proccess-name-by-id/${id}?${qs.toString()}`
            );

            if (res?.data?.status === 1) {
                const rows = Array.isArray(res?.data?.process) ? res.data.process : [];

                const normalized = rows.map((r) => ({
                    ...r,

                    // identity / filters
                    skillname: normalizeSkill(r?.skillname),
                    team_name: deriveTeamName(r?.skillname),
                    center_name: deriveCenterName(r?.skillname),

                    // totals
                    calls_unique_skill_server: Number(r?.calls_unique_skill_server) || 0,
                    calls_unique_exclusive: Number(r?.calls_unique_exclusive) || 0,
                    orders_count: Number(r?.orders_count) || 0,
                    value: Number(r?.value) || 0,
                    delivered_value: Number(r?.delivered_value) || 0,
                    delivered_count: Number(r?.delivered_count) || 0,

                    // ✅ hour-wise maps
                    ppc_spend_hourly: r?.ppc_spend_hourly || {},
                    calls_unique_exclusive_hourly: r?.calls_unique_exclusive_hourly || {},
                    calls_unique_skill_server_hourly: r?.calls_unique_skill_server_hourly || {},
                    orders_count_hourly: r?.orders_count_hourly || {},
                    delivered_value_hourly: r?.delivered_value_hourly || {},
                    delivered_count_hourly: r?.delivered_count_hourly || {},
                    value_hourly: r?.value_hourly || {},

                    tkt_size: Number(r?.tkt_size) || 0,
                    del_pct: Number(r?.del_pct) || 0,
                    ppc_cost_per_call: Number(r?.ppc_cost_per_call) || 0,
                }));

                setProcess(normalized);
            } else {
                toast.error(res?.data?.message || "Failed to load report");
                setProcess([]);
            }
        } catch (e) {
            console.error(e);
            toast.error("Network/CORS error while loading report");
            setProcess([]);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ initial load: today, 1..24
    useEffect(() => {
        if (!ppcid) return navigate("/");
        const today = getToday();
        setAppliedDate(today);
        setAppliedHours({ from: 1, to: 24 });
        loadReport(ppcid, today, 1, 24);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ppcid]);

    // ✅ dropdown options from data
    const teamOptions = useMemo(() => {
        const opts = uniqSorted(process.map((x) => x.team_name)).filter((x) => x !== "Other");
        return opts.length ? opts : ["Other"];
    }, [process]);

    const centerOptions = useMemo(() => {
        const opts = uniqSorted(process.map((x) => x.center_name)).filter((x) => x !== "Other");
        return opts;
    }, [process]);

    const skillOptions = useMemo(() => uniqSorted(process.map((x) => x.skillname)), [process]);

    // ✅ init selects
    useEffect(() => {
        setSelectedTeams(new Set());
        setAppliedTeams(new Set());
    }, [teamOptions]);

    useEffect(() => {
        setSelectedCenters(new Set());
        setAppliedCenters(new Set());
    }, [centerOptions]);

    useEffect(() => {
        setSelectedSkills(new Set());
        setAppliedSkills(new Set());
    }, [skillOptions]);

    const filteredTeamOptions = useMemo(() => filterOptions(teamOptions, teamSearch), [teamOptions, teamSearch]);
    const filteredCenterOptions = useMemo(() => filterOptions(centerOptions, centerSearch), [centerOptions, centerSearch]);
    const filteredSkillOptions = useMemo(() => filterOptions(skillOptions, skillSearch), [skillOptions, skillSearch]);

    const toggleSetItem = (setter, value) => {
        setter((prev) => {
            const next = new Set(prev);
            if (next.has(value)) next.delete(value);
            else next.add(value);
            return next;
        });
    };

    const btnText = (appliedSet, allOptions) => {
        if (!appliedSet || appliedSet.size === 0) return "All";
        if (appliedSet.size === allOptions.length) return "All";
        if (appliedSet.size === 1) return Array.from(appliedSet)[0];
        return `${appliedSet.size} Selected`;
    };

    // ✅ UI filtering ONLY via dropdowns
    const filteredProcess = useMemo(() => {
        const tset = appliedTeams.size ? appliedTeams : new Set(teamOptions);
        const cset = appliedCenters.size ? appliedCenters : new Set(centerOptions);
        const sset = appliedSkills.size ? appliedSkills : new Set(skillOptions);

        return (process || []).filter((r) => tset.has(r.team_name) && cset.has(r.center_name) && sset.has(r.skillname));
    }, [process, appliedTeams, appliedCenters, appliedSkills, teamOptions, centerOptions, skillOptions]);

    // ✅ totals (grand)
    const totals = useMemo(() => {
        const n = (v) => Number(v) || 0;
        return filteredProcess.reduce(
            (acc, item) => {
                acc.calls_unique_skill_server += n(item.calls_unique_skill_server);
                acc.calls_unique_exclusive += n(item.calls_unique_exclusive);
                acc.orders_count += n(item.orders_count);
                acc.value += n(item.value);

                // total cost = sum spend_hourly (because API is hour range based)
                const spendHourly = item.ppc_spend_hourly || {};
                acc.ppc_spend_total += Object.values(spendHourly).reduce((s, x) => s + n(x), 0);

                acc.delivered_value += n(item.delivered_value);
                acc.delivered_count += n(item.delivered_count);
                acc.rows += 1;
                return acc;
            },
            {
                calls_unique_skill_server: 0,
                calls_unique_exclusive: 0,
                orders_count: 0,
                value: 0,
                ppc_spend_total: 0,
                delivered_value: 0,
                delivered_count: 0,
                rows: 0,
            }
        );
    }, [filteredProcess]);

    // ✅ KPIs (grand)
    const grandConvPct = totals.calls_unique_exclusive > 0 ? (totals.orders_count / totals.calls_unique_exclusive) * 100 : 0;
    const grandDelPct = totals.value > 0 ? (totals.delivered_value / totals.value) * 100 : 0;
    const grandTicket = totals.orders_count > 0 ? totals.value / totals.orders_count : 0;
    const grandTotalCost = totals.ppc_spend_total;
    const grandCallCost = totals.calls_unique_exclusive > 0 ? grandTotalCost / totals.calls_unique_exclusive : 0;
    const grandFactor = grandTotalCost > 0 ? totals.value / grandTotalCost : 0;

    // ✅ hour options
    const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => i + 1), []);

    const safeNum = (v) => {
        const n = Number(v);
        return Number.isFinite(n) ? n : 0;
    };

    const hourLabel = (h) => {
        // h = 1..24, map to nice label
        const hh = h - 1; // 0..23
        const from = new Date(2000, 0, 1, hh, 0, 0);
        const to = new Date(2000, 0, 1, hh, 59, 59);
        const a = from.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        const b = to.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" });
        return `${a} - ${b}`;
    };

    // ✅ Build DAY -> HOUR rows
    const dayHourRows = useMemo(() => {
        // only one day (selected), but structure remains expandable
        const d = appliedDate;

        const hFrom = appliedHours.from;
        const hTo = appliedHours.to;

        const hours = [];
        for (let h = hFrom; h <= hTo; h++) hours.push(h);

        // totals per day (sum of hours)
        const dayTotals = {
            date: d,
            calls_unique_skill_server: 0,
            calls_unique_exclusive: 0,
            orders_count: 0,
            value: 0,
            delivered_value: 0,
            delivered_count: 0,
            ppc_spend_total: 0,
        };

        // hour wise array
        const hourRows = hours.map((h) => ({
            hour: h,
            calls_unique_skill_server: 0,
            calls_unique_exclusive: 0,
            orders_count: 0,
            value: 0,
            delivered_value: 0,
            delivered_count: 0,
            ppc_spend_total: 0,
        }));

        const idxByHour = new Map(hours.map((h, idx) => [h, idx]));

        (filteredProcess || []).forEach((item) => {
            const spend = item.ppc_spend_hourly || {};
            const cEx = item.calls_unique_exclusive_hourly || {};
            const cSkill = item.calls_unique_skill_server_hourly || {};
            const ord = item.orders_count_hourly || {};
            const delV = item.delivered_value_hourly || {};
            const delC = item.delivered_count_hourly || {};
            const val = item.value_hourly || {};

            hours.forEach((h) => {
                const idx = idxByHour.get(h);
                if (idx === undefined) return;

                hourRows[idx].ppc_spend_total += safeNum(spend[h]);
                hourRows[idx].calls_unique_exclusive += safeNum(cEx[h]);
                hourRows[idx].calls_unique_skill_server += safeNum(cSkill[h]);
                hourRows[idx].orders_count += safeNum(ord[h]);
                hourRows[idx].delivered_value += safeNum(delV[h]);
                hourRows[idx].delivered_count += safeNum(delC[h]);
                hourRows[idx].value += safeNum(val[h]);
            });
        });

        // compute day totals
        hourRows.forEach((r) => {
            dayTotals.calls_unique_skill_server += safeNum(r.calls_unique_skill_server);
            dayTotals.calls_unique_exclusive += safeNum(r.calls_unique_exclusive);
            dayTotals.orders_count += safeNum(r.orders_count);
            dayTotals.value += safeNum(r.value);
            dayTotals.delivered_value += safeNum(r.delivered_value);
            dayTotals.delivered_count += safeNum(r.delivered_count);
            dayTotals.ppc_spend_total += safeNum(r.ppc_spend_total);
        });

        return {
            dayKey: d,
            dayLabel: new Date(d + "T00:00:00").toLocaleString("en-US", {
                weekday: "long",
                month: "long",
                day: "2-digit",
                year: "numeric",
            }),
            totals: dayTotals,
            rows: hourRows,
        };
    }, [filteredProcess, appliedDate, appliedHours.from, appliedHours.to]);

    const onApply = () => {
        if (!selectedDate) return toast.error("Please select Date");
        if (fromHour > toHour) return toast.error("From Hour cannot be greater than To Hour");

        setAppliedDate(selectedDate);
        setAppliedHours({ from: fromHour, to: toHour });

        setAppliedTeams(new Set(selectedTeams.size ? selectedTeams : []));
        setAppliedCenters(new Set(selectedCenters.size ? selectedCenters : []));
        setAppliedSkills(new Set(selectedSkills.size ? selectedSkills : []));

        setTeamOpen(false);
        setCenterOpen(false);
        setSkillOpen(false);

        loadReport(ppcid, selectedDate, fromHour, toHour);
    };

    const onReset = () => {
        const today = getToday();

        setSelectedDate(today);
        setAppliedDate(today);

        setFromHour(1);
        setToHour(24);
        setAppliedHours({ from: 1, to: 24 });

        const tAll = new Set(teamOptions);
        const cAll = new Set(centerOptions);
        const sAll = new Set(skillOptions);

        setSelectedTeams(tAll);
        setAppliedTeams(tAll);

        setSelectedCenters(cAll);
        setAppliedCenters(cAll);

        setSelectedSkills(sAll);
        setAppliedSkills(sAll);

        setTeamSearch("");
        setCenterSearch("");
        setSkillSearch("");

        setTeamOpen(false);
        setCenterOpen(false);
        setSkillOpen(false);

        loadReport(ppcid, today, 1, 24);
    };

    const open = expandedDays.has(dayHourRows.dayKey);

    return (
        <div className="misDashboard" ref={wrapRef}>
            <Helmet>
                <title>SATKARTAR::PROCESS REPORT</title>
            </Helmet>

            <style>{`
        .filterRow{display:flex;gap:8px;align-items:end;flex-wrap:wrap;}
        .filterBox{display:flex;flex-direction:column;gap:4px;}
        .filterBox label{font-weight:600;font-size:12px;margin:0;}
        .dropWrap{position:relative; min-width:260px;}
        .dropBtn{
          width:260px;display:flex;align-items:center;justify-content:space-between;
          padding:6px 10px;border:1px solid rgba(0,0,0,.15);border-radius:6px;
          background:#fff;font-size:13px;
        }
        .dropMenu{
          position:absolute; top:100%; left:0; z-index:50; width:260px;
          background:#fff;border:1px solid rgba(0,0,0,.15);border-radius:10px;
          margin-top:6px;box-shadow:0 10px 30px rgba(0,0,0,.12);overflow:hidden;
        }
        .dropHead{padding:10px; border-bottom:1px solid rgba(0,0,0,.08);}
        .dropSearch{
          width:100%;border:1px solid rgba(0,0,0,.15);border-radius:8px;
          padding:7px 10px;font-size:13px;outline:none;
        }
        .dropActions{
          display:flex; padding:10px;border-bottom:1px solid rgba(0,0,0,.08);
          justify-content:space-between;
        }
        .dropList{max-height:240px; overflow:auto; padding:8px 5px;}
        .dropItem{display:flex; align-items:center; gap:10px; padding:6px 2px; font-size:13px;}
        .dropFoot{
          display:flex; gap:8px; justify-content:space-between; padding:10px;
          border-top:1px solid rgba(0,0,0,.08); background:#fafafa;
        }

        .caretBtn{
          width:18px;height:18px;display:inline-flex;align-items:center;justify-content:center;
          border:1px solid rgba(0,0,0,.2);border-radius:4px;background:#fff;cursor:pointer;
          font-size:12px;line-height:1;
        }
        .dayRow td{ background:#f3f6fb !important; font-weight:700; }
        .treeCell{display:flex;align-items:center;gap:8px;}
        .hourIndent{padding-left:26px;}
      `}</style>

            <ContentWrapper>
                <div className="row">
                    <div className="col-12">
                        <ReportNavBar />
                    </div>

                    <div className="col-12 col-lg-12 col-sm-6">
                        <div className="card hrpages">
                            <div className="card shadow-sm mt-2 mx-2">
                                <div className="card-body p-2">
                                    <div className="filterRow">
                                        <div className="filterBox">
                                            <label>Date</label>
                                            <input
                                                type="date"
                                                className="form-control form-control-sm"
                                                value={selectedDate}
                                                onChange={(e) => setSelectedDate(e.target.value)}
                                                disabled={isLoading}
                                            />
                                        </div>

                                        <div className="filterBox">
                                            <label>From Hour</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={fromHour}
                                                onChange={(e) => setFromHour(Number(e.target.value))}
                                                disabled={isLoading}
                                            >
                                                {hourOptions.map((h) => (
                                                    <option key={h} value={h}>{h}</option>
                                                ))}
                                            </select>
                                        </div>

                                        <div className="filterBox">
                                            <label>To Hour</label>
                                            <select
                                                className="form-control form-control-sm"
                                                value={toHour}
                                                onChange={(e) => setToHour(Number(e.target.value))}
                                                disabled={isLoading}
                                            >
                                                {hourOptions.map((h) => (
                                                    <option key={h} value={h}>{h}</option>
                                                ))}
                                            </select>
                                        </div>

                                        {/* Team */}
                                        <div className="filterBox">
                                            <label>Team Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setTeamOpen((s) => !s);
                                                        setCenterOpen(false);
                                                        setSkillOpen(false);
                                                    }}
                                                    disabled={isLoading || !teamOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedTeams, teamOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {teamOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={teamSearch}
                                                                onChange={(e) => setTeamSearch(e.target.value)}
                                                                placeholder="Search team..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedTeams(new Set(teamOptions))}>
                                                                Select All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => setSelectedTeams(new Set())}
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredTeamOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedTeams.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedTeams, name)}
                                                                    />
                                                                    <span>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(teamOptions);
                                                                    setSelectedTeams(all);
                                                                    setAppliedTeams(all);
                                                                    setTeamSearch("");
                                                                    setTeamOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedTeams.size ? new Set(selectedTeams) : new Set(teamOptions);
                                                                    setAppliedTeams(all);
                                                                    setTeamOpen(false);
                                                                }}
                                                            >
                                                                Apply Teams
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}

                                            </div>
                                        </div>

                                        {/* Center */}
                                        <div className="filterBox">
                                            <label>Center Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setCenterOpen((s) => !s);
                                                        setTeamOpen(false);
                                                        setSkillOpen(false);
                                                    }}
                                                    disabled={isLoading || !centerOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedCenters, centerOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {centerOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={centerSearch}
                                                                onChange={(e) => setCenterSearch(e.target.value)}
                                                                placeholder="Search center..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedCenters(new Set(centerOptions))}>
                                                                Select All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => setSelectedTeams(new Set())}
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredCenterOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedCenters.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedCenters, name)}
                                                                    />
                                                                    <span>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(centerOptions);
                                                                    setSelectedCenters(all);
                                                                    setAppliedCenters(all);
                                                                    setCenterSearch("");
                                                                    setCenterOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedCenters.size ? new Set(selectedCenters) : new Set(centerOptions);
                                                                    setAppliedCenters(all);
                                                                    setCenterOpen(false);
                                                                }}
                                                            >
                                                                Apply Centers
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        {/* Skill */}
                                        <div className="filterBox">
                                            <label>Skill Name</label>
                                            <div className="dropWrap">
                                                <button
                                                    type="button"
                                                    className="dropBtn"
                                                    onClick={() => {
                                                        setSkillOpen((s) => !s);
                                                        setTeamOpen(false);
                                                        setCenterOpen(false);
                                                    }}
                                                    disabled={isLoading || !skillOptions.length}
                                                >
                                                    <span style={{ fontWeight: 600 }}>{btnText(appliedSkills, skillOptions)}</span>
                                                    <span style={{ opacity: 0.7 }}>▾</span>
                                                </button>

                                                {skillOpen && (
                                                    <div className="dropMenu">
                                                        <div className="dropHead">
                                                            <input
                                                                className="dropSearch"
                                                                value={skillSearch}
                                                                onChange={(e) => setSkillSearch(e.target.value)}
                                                                placeholder="Search skill..."
                                                            />
                                                        </div>

                                                        <div className="dropActions">
                                                            <button type="button" className="btnselectall" onClick={() => setSelectedSkills(new Set(skillOptions))}>
                                                                Select All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => setSelectedTeams(new Set())}
                                                            >
                                                                Clear
                                                            </button>
                                                        </div>

                                                        <div className="dropList">
                                                            {filteredSkillOptions.map((name) => (
                                                                <label key={name} className="dropItem">
                                                                    <input
                                                                        type="checkbox"
                                                                        checked={selectedSkills.has(name)}
                                                                        onChange={() => toggleSetItem(setSelectedSkills, name)}
                                                                    />
                                                                    <span title={name}>{name}</span>
                                                                </label>
                                                            ))}
                                                        </div>

                                                        <div className="dropFoot">
                                                            <button
                                                                type="button"
                                                                className="btnselectall"
                                                                onClick={() => {
                                                                    const all = new Set(skillOptions);
                                                                    setSelectedSkills(all);
                                                                    setAppliedSkills(all);
                                                                    setSkillSearch("");
                                                                    setSkillOpen(false);
                                                                }}
                                                            >
                                                                Reset All
                                                            </button>
                                                            <button
                                                                type="button"
                                                                className="btnresetall"
                                                                onClick={() => {
                                                                    const all = selectedSkills.size ? new Set(selectedSkills) : new Set(skillOptions);
                                                                    setAppliedSkills(all);
                                                                    setSkillOpen(false);
                                                                }}
                                                            >
                                                                Apply Skills
                                                            </button>
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                        </div>

                                        <button className="btnskillapply" onClick={onApply} disabled={isLoading}>
                                            Apply
                                        </button>

                                        <button className="btnresetall" onClick={onReset} disabled={isLoading}>
                                            Reset
                                        </button>
                                    </div>
                                </div>
                            </div>

                            {/* ✅ TABLE */}
                            <div className="table pagetable table-responsive mt-2">
                                <Table className="table-sm table-fixeds" responsive striped bordered hover>
                                    {/* ✅ Grand Total row */}
                                    <thead>
                                        <tr>
                                            <th colSpan={2} className="text-end" style={{ fontWeight: 700, fontSize: 14 }}>
                                                Grand Total
                                            </th>

                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.calls_unique_skill_server}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.calls_unique_exclusive}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.orders_count}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(totals.value)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandTotalCost)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(totals.delivered_value)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{totals.delivered_count}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandConvPct.toFixed(2)}%</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandCallCost)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandDelPct.toFixed(2)}%</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{numberFormat(grandTicket)}</th>
                                            <th style={{ fontWeight: 700, fontSize: 14 }}>{grandFactor.toFixed(2)}</th>
                                        </tr>
                                    </thead>

                                    <thead>
                                        <tr>
                                            <th>#</th>
                                            <th>Day / Hour</th>
                                            <th>Call(Skill)</th>
                                            <th>Calls(All Skill)</th>
                                            <th>Count of Orders</th>
                                            <th>Volume</th>
                                            <th>Total Cost</th>
                                            <th>Delivered</th>
                                            <th>Del Count</th>
                                            <th>Conv(%)</th>
                                            <th>Call/Cost</th>
                                            <th>Del(%)</th>
                                            <th>Ticket Size</th>
                                            <th>Factor</th>
                                        </tr>
                                    </thead>

                                    <tbody>
                                        {isLoading ? (
                                            [...Array(10)].map((_, i) => (
                                                <tr key={i}>
                                                    {Array.from({ length: 14 }).map((__, idx) => (
                                                        <td key={idx}>
                                                            <Skeleton animation="wave" />
                                                        </td>
                                                    ))}
                                                </tr>
                                            ))
                                        ) : (
                                            <>
                                                {/* ✅ Day Row */}
                                                <tr className="dayRow">
                                                    <td></td>
                                                    <td style={{ fontWeight: "600" }}>
                                                        <div className="treeCell">
                                                            <button className="caretBtn" type="button" onClick={() => toggleDay(dayHourRows.dayKey)}>
                                                                {open ? "−" : "+"}
                                                            </button>
                                                            <span>{dayHourRows.dayLabel}</span>
                                                        </div>
                                                    </td>

                                                    <td style={{ fontWeight: "600" }}>{dayHourRows.totals.calls_unique_skill_server}</td>
                                                    <td style={{ fontWeight: "600" }}>{dayHourRows.totals.calls_unique_exclusive}</td>
                                                    <td style={{ fontWeight: "600" }}>{dayHourRows.totals.orders_count}</td>
                                                    <td style={{ fontWeight: "600" }}>{numberFormat(dayHourRows.totals.value)}</td>
                                                    <td style={{ fontWeight: "600" }}>{numberFormat(dayHourRows.totals.ppc_spend_total)}</td>
                                                    <td style={{ fontWeight: "600" }}>{numberFormat(dayHourRows.totals.delivered_value)}</td>
                                                    <td style={{ fontWeight: "600" }}>{dayHourRows.totals.delivered_count}</td>

                                                    {(() => {
                                                        const convPct =
                                                            dayHourRows.totals.calls_unique_exclusive > 0
                                                                ? (dayHourRows.totals.orders_count / dayHourRows.totals.calls_unique_exclusive) * 100
                                                                : 0;

                                                        const delPct =
                                                            dayHourRows.totals.value > 0
                                                                ? (dayHourRows.totals.delivered_value / dayHourRows.totals.value) * 100
                                                                : 0;

                                                        const ticket =
                                                            dayHourRows.totals.orders_count > 0
                                                                ? dayHourRows.totals.value / dayHourRows.totals.orders_count
                                                                : 0;

                                                        const callCost =
                                                            dayHourRows.totals.calls_unique_exclusive > 0
                                                                ? dayHourRows.totals.ppc_spend_total / dayHourRows.totals.calls_unique_exclusive
                                                                : 0;

                                                        const factor =
                                                            dayHourRows.totals.ppc_spend_total > 0
                                                                ? dayHourRows.totals.value / dayHourRows.totals.ppc_spend_total
                                                                : 0;

                                                        return (
                                                            <>
                                                                <td style={{ fontWeight: "600" }}>{convPct.toFixed(2)}%</td>
                                                                <td style={{ fontWeight: "600" }}>{numberFormat(callCost)}</td>
                                                                <td style={{ fontWeight: "600" }}>{delPct.toFixed(2)}%</td>
                                                                <td style={{ fontWeight: "600" }}>{numberFormat(ticket)}</td>
                                                                <td style={{ fontWeight: "600" }}>{factor.toFixed(2)}</td>
                                                            </>
                                                        );
                                                    })()}
                                                </tr>

                                                {/* ✅ Hour rows */}
                                                {open &&
                                                    dayHourRows.rows.map((hRow, idx) => {
                                                        const conv = hRow.calls_unique_exclusive > 0 ? (hRow.orders_count / hRow.calls_unique_exclusive) * 100 : 0;
                                                        const del = hRow.value > 0 ? (hRow.delivered_value / hRow.value) * 100 : 0;
                                                        const ticket = hRow.orders_count > 0 ? hRow.value / hRow.orders_count : 0;
                                                        const callCost = hRow.calls_unique_exclusive > 0 ? hRow.ppc_spend_total / hRow.calls_unique_exclusive : 0;
                                                        const factor = hRow.ppc_spend_total > 0 ? hRow.value / hRow.ppc_spend_total : 0;

                                                        return (
                                                            <tr key={hRow.hour}>
                                                                <td>{idx + 1}</td>
                                                                <td className="hourIndent">{hourLabel(hRow.hour)}</td>

                                                                <td>{hRow.calls_unique_skill_server}</td>
                                                                <td>{hRow.calls_unique_exclusive}</td>
                                                                <td>{hRow.orders_count}</td>
                                                                <td>{numberFormat(hRow.value)}</td>
                                                                <td>{numberFormat(hRow.ppc_spend_total)}</td>
                                                                <td>{numberFormat(hRow.delivered_value)}</td>
                                                                <td>{hRow.delivered_count}</td>
                                                                <td>{conv.toFixed(2)}%</td>
                                                                <td>{numberFormat(callCost)}</td>
                                                                <td>{del.toFixed(2)}%</td>
                                                                <td>{numberFormat(ticket)}</td>
                                                                <td>{factor.toFixed(2)}</td>
                                                            </tr>
                                                        );
                                                    })}

                                                {!dayHourRows.rows.length && (
                                                    <tr>
                                                        <td colSpan={14} className="text-center">
                                                            No Data
                                                        </td>
                                                    </tr>
                                                )}
                                            </>
                                        )}
                                    </tbody>
                                </Table>
                            </div>

                            <div className="px-3 pb-2" style={{ fontSize: 12, opacity: 0.75 }}>
                                Showing: <b>{appliedDate}</b> | Hours: <b>{appliedHours.from}</b> to <b>{appliedHours.to}</b>
                            </div>
                        </div>
                    </div>
                </div >
            </ContentWrapper >
        </div >
    );
};

export default withAuth(HourWiseReport);
